import type { ParticipantView } from '@seen/core/models/participantView'
import { omit } from 'lodash-es'
import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'
import type { Filter } from '#src/components/filter/Filter.tsx'
import { migrateParticipantsPagePersistedState } from '#src/pages/participants/participantsPageView.ts'

export type ParticipantPageView =
  | 'all'
  | 'daily-attendance'
  | 'day-center-am'
  | 'day-center-pm'
  | ParticipantView['referenceString']

export interface ParticipantViewDisplay {
  ordering: 'name' | 'enrollment-date'
  showLabels: boolean
  showBirthDate: boolean
  showPhoneNumber: boolean
  showMRN: boolean
  showEnrollmentDate: boolean
}

export const DEFAULT_DISPLAY: ParticipantViewDisplay = {
  ordering: 'name',
  showLabels: false,
  showBirthDate: false,
  showPhoneNumber: false,
  showMRN: false,
  showEnrollmentDate: false,
}

export const DEFAULT_FILTERS: Filter[] = []

export const useParticipantsPageStore = create(
  devtools(
    persist(
      combine(
        {
          view: 'all' as ParticipantPageView,
          filtersByView: {} as Record<string, Filter[] | undefined>,
          displayByView: {} as Record<
            string,
            Partial<ParticipantViewDisplay> | undefined
          >,
        },
        (set) => ({
          setView: (
            nextView:
              | ParticipantPageView
              | ((currentView: ParticipantPageView) => ParticipantPageView)
          ) => {
            set((state) => ({
              view:
                typeof nextView === 'function'
                  ? nextView(state.view)
                  : nextView,
            }))
          },
          setFilters: ({
            filters,
            forView,
          }: {
            filters:
              | Filter[]
              | ((currentFilters: Filter[] | undefined) => Filter[])
            forView: ParticipantPageView
          }) => {
            set((state) => ({
              filtersByView: {
                ...state.filtersByView,
                [forView]:
                  typeof filters === 'function'
                    ? filters(state.filtersByView[forView])
                    : filters,
              },
            }))
          },
          unsetFilters: (view: ParticipantPageView) => {
            set((state) => ({
              filtersByView: omit(state.filtersByView, view),
            }))
          },
          setDisplay: ({
            display,
            forView,
          }: {
            display:
              | Partial<ParticipantViewDisplay>
              | ((
                  currentDisplay: Partial<ParticipantViewDisplay>
                ) => Partial<ParticipantViewDisplay>)
            forView: ParticipantPageView
          }) => {
            set((state) => ({
              displayByView: {
                ...state.displayByView,
                [forView]:
                  typeof display === 'function'
                    ? display(state.displayByView[forView] ?? {})
                    : display,
              },
            }))
          },
          unsetDisplay: (view: ParticipantPageView) => {
            set((state) => ({
              displayByView: omit(state.displayByView, view),
            }))
          },
        })
      ),
      {
        name: 'participants-page',
        version: 1,
        migrate: (persisted) =>
          migrateParticipantsPagePersistedState(persisted as { view?: string }),
      }
    )
  )
)
